import { useEffect, useState, useCallback } from "react";
import { KeyRound, Activity, CheckCircle2, XCircle, ShieldAlert, Clock, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";

const fmtTime = (ts) => (ts ? new Date(ts).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const AXIS_TICK = { fontSize: 10 };
const BAR_RADIUS = [4, 4, 0, 0];
const tickDay = (d) => d.slice(5);

function StatusBadge({ status, valid }) {
  const ok = valid && status >= 200 && status < 400;
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
      {status}
    </span>
  );
}

export default function AdminApiAnalytics() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: l }] = await Promise.all([
        api.get("/admin/api-key-stats"),
        api.get("/admin/api-key-logs", { params: { limit: 200, ...(selected ? { key_id: selected } : {}) } }),
      ]);
      setStats(s);
      setLogs(l);
    } catch (e) { console.warn("api analytics load failed", e); }
    finally { setLoading(false); }
  }, [selected]);

  useEffect(() => { load(); }, [load]);

  const keyStats = selected ? stats?.keys.find((k) => k.key_id === selected) : null;
  const cards = keyStats || stats?.overall;
  const chartData = keyStats?.daily || [];
  const endpoints = keyStats?.endpoints || [];

  return (
    <div className="space-y-5" data-testid="api-analytics-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <KeyRound className="text-indigo-600" size={22} />
          <div>
            <h1 className="text-lg font-extrabold text-slate-900">API Key Analytics</h1>
            <p className="text-xs text-slate-500">Har integration key ka call-history aur usage — last 7 din (logs 30 din tak rehte hain)</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <select data-testid="analytics-key-filter" value={selected} onChange={(e) => setSelected(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white min-w-0 flex-1 sm:flex-none">
            <option value="">All Keys (overall)</option>
            {stats?.keys.map((k) => (
              <option key={k.key_id} value={k.key_id}>{k.key_name} ({k.masked || "unknown"})</option>
            ))}
          </select>
          <button data-testid="analytics-refresh-btn" onClick={load} disabled={loading}
            className="px-3 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-60">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-total-calls">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 uppercase"><Activity size={12} /> Total Calls</div>
          <p className="text-2xl font-extrabold text-slate-900 mt-1">{cards?.total ?? "—"}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-success-calls">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 uppercase"><CheckCircle2 size={12} className="text-emerald-500" /> Success</div>
          <p className="text-2xl font-extrabold text-emerald-600 mt-1">{cards?.success ?? "—"}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-error-calls">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 uppercase"><XCircle size={12} className="text-amber-500" /> Errors (4xx/5xx)</div>
          <p className="text-2xl font-extrabold text-amber-600 mt-1">{cards?.errors ?? "—"}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-failed-auth">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 uppercase"><ShieldAlert size={12} className="text-rose-500" /> Failed Attempts</div>
          <p className="text-2xl font-extrabold text-rose-600 mt-1">{cards?.failed_auth ?? "—"}</p>
          <p className="text-[10px] text-slate-400">invalid / revoked key</p>
        </div>
      </div>

      {selected && keyStats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="analytics-daily-chart">
            <p className="text-sm font-bold text-slate-800 mb-3">7-Day Call Trend — {keyStats.key_name}</p>
            {chartData.length ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={AXIS_TICK} tickFormatter={tickDay} />
                  <YAxis allowDecimals={false} tick={AXIS_TICK} width={28} />
                  <Tooltip />
                  <Bar dataKey="calls" fill="#4f46e5" radius={BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-slate-400 py-8 text-center">Is key se last 7 din me koi call nahi</p>}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="analytics-top-endpoints">
            <p className="text-sm font-bold text-slate-800 mb-3">Top Endpoints</p>
            {endpoints.length ? (
              <div className="space-y-1.5">
                {endpoints.map((e) => (
                  <div key={e.endpoint} className="flex items-center justify-between text-xs">
                    <code className="text-slate-600 bg-slate-50 px-2 py-1 rounded truncate">{e.endpoint}</code>
                    <span className="font-bold text-slate-800 ml-2 shrink-0">{e.calls}×</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-slate-400 py-8 text-center">Koi endpoint data nahi</p>}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <p className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1.5"><Clock size={14} className="text-slate-400" /> Call History {selected ? "" : "(sabhi keys)"}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="api-logs-table">
            <thead>
              <tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-3">Time</th><th className="py-2 pr-3">Key</th><th className="py-2 pr-3">Method</th>
                <th className="py-2 pr-3">Endpoint</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">ms</th><th className="py-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} data-testid={`api-log-${l.id}`} className={`border-b border-slate-50 ${!l.valid ? "bg-rose-50/50" : ""}`}>
                  <td className="py-2 pr-3 whitespace-nowrap text-slate-500">{fmtTime(l.ts)}</td>
                  <td className="py-2 pr-3">
                    <span className="font-semibold text-slate-700">{l.key_name || "Unknown"}</span>
                    {!l.valid && <span className="ml-1 text-[9px] font-bold text-rose-600">INVALID</span>}
                  </td>
                  <td className="py-2 pr-3 font-mono font-bold text-slate-600">{l.method}</td>
                  <td className="py-2 pr-3 font-mono text-slate-600 max-w-[220px] truncate">{l.path}</td>
                  <td className="py-2 pr-3"><StatusBadge status={l.status} valid={l.valid} /></td>
                  <td className="py-2 pr-3 text-slate-500">{l.ms}</td>
                  <td className="py-2 text-slate-400">{l.ip || "—"}</td>
                </tr>
              ))}
              {!logs.length && (
                <tr><td colSpan={7} className="py-10 text-center text-slate-400" data-testid="api-logs-empty">
                  Abhi tak koi API-key call record nahi hui — jab koi integration X-API-Key se call karega, yahan dikhega.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
